import React, { Component, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  useLocation,
  Navigate,
  useParams,
} from "react-router-dom"; // Import useLocation
import ProfileCard from "./components/ProfileCard";
import Header from "./components/Header";
import ShowUserDetails from "./components/ShowUserDetails";
// Home stays a static import — it's the most common landing page, so it should be part of
// the initial bundle rather than costing every first-time visitor an extra chunk round-trip.
// Every other page is lazy: previously all ~20 pages (dashboards, college details, compare,
// articles, admin-ish pages, etc.) shipped in one 966KB bundle regardless of which page a
// visitor actually landed on. Splitting them means a Home visitor only downloads Home's code.
import Home from "./pages/HomePage";
import Footer from "./components/Footer";
import Cookies from "js-cookie";
import { getAccountType } from "./utility/accountType";
import EnquiryPopupForm from "./components/EnquiryPopUpForm";
import WhatsAppFloatingWidget from "./components/WhatsAppFloatingWidget";
import AiChatWidget from "./components/AiChatWidget";
import PageTransition from "./components/ui/PageTransition";
import PageLoader from "./components/ui/PageLoader";
import { GoogleOAuthProvider } from "@react-oauth/google";
import CompareTray from "./components/CompareTray";
import { CompareProvider } from "./context/CompareContext";
import { ShortlistProvider } from "./context/ShortlistContext";
import { NotificationProvider } from "./context/NotificationContext";

const CollegeListPage = lazy(() => import("./pages/CollegeListPage"));
const CollegeDetailsPage = lazy(() => import("./pages/CollegeDetailsPage"));
const SearchResultsPage = lazy(() => import("./pages/SearchResultsPage"));
const StudentDashboardPage = lazy(() => import("./pages/StudentDashboardPage"));
const CollegeDashboardPage = lazy(() => import("./pages/CollegeDashboardPage"));
const AuthorDashboardPage = lazy(() => import("./pages/AuthorDashboardPage"));
const ArticleListPage = lazy(() => import("./pages/ArticleListPage"));
const ArticleDetailPage = lazy(() => import("./pages/ArticleDetailPage"));
const ExpertProfilePage = lazy(() => import("./pages/ExpertProfilePage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const AdmissionAssistancePage = lazy(() => import("./pages/AdmissionAssistancePage"));
const StudyAbroadPage = lazy(() => import("./pages/StudyAbroadPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const CutoffSearchPage = lazy(() => import("./pages/CutoffSearchPage"));
const EligibilityCheckerPage = lazy(() => import("./pages/EligibilityCheckerPage"));
const EditStudentProfilePage = lazy(() => import("./pages/EditStudentProfilePage"));

const EnquiryPopupConditional = ({ onClose }) => {
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const token = Cookies.get("token");
  const accountType = Cookies.get("accountType");
  const isCollege = token && accountType === "college";
  const isAdmin = token && accountType === "admin";
  const isAuthor = token && accountType === "author";
  const canEnquire = !isCollege && !isAdmin && !isAuthor;
  return isHomePage && canEnquire ? <EnquiryPopupForm onClose={onClose} /> : null;
};

// Backward-compatible redirect for older/shorter URLs like /college/mba/cat
// Redirects to the currently supported list URL shape: /college/:course/top/:menuName
// Preserves the query string (e.g. ?city=Mumbai from the nav menu) — dropping it
// here silently broke every location/type filter for any menu link shaped this way.
const CollegeListRedirect = () => {
  const { course, menuName } = useParams();
  const location = useLocation();
  return <Navigate to={`/college/${course}/top/${menuName}${location.search}`} replace />;
};

// Backward-compatible redirect for older “college by location” URLs served by the menu API.
// Example: /college/medical/college/location/medical-colleges-india
// Redirects to: /college/medical/top/medical-colleges-india
const CollegeByLocationRedirect = () => {
  const { course, menuName } = useParams();
  const location = useLocation();
  return <Navigate to={`/college/${course}/top/${menuName}${location.search}`} replace />;
};

class App extends Component {
  constructor(props) {
    super(props);

    let isAdminFromCookie = false;
    try {
      const roles = JSON.parse(Cookies.get("roles") || "[]");
      isAdminFromCookie = Array.isArray(roles) && roles.includes("ROLE_ADMIN");
    } catch {
      isAdminFromCookie = false;
    }

    const hasToken = Boolean(Cookies.get("token"));
    this.state = {
      isAdminLoggedIn: isAdminFromCookie,
      isDefaultLoggedIn: hasToken,
      showForm: false,
    };
    this.sessionTimeout = null;
    this.syncAuthFromCookies = this.syncAuthFromCookies.bind(this);
  }

  componentDidMount() {
    this.timer = setTimeout(() => {
      this.setState({ showForm: true });
    }, 10000);

    window.addEventListener("focus", this.syncAuthFromCookies);
    document.addEventListener("visibilitychange", this.syncAuthFromCookies);
  }

  componentWillUnmount() {
    clearTimeout(this.timer);
    window.removeEventListener("focus", this.syncAuthFromCookies);
    document.removeEventListener("visibilitychange", this.syncAuthFromCookies);
  }

  syncAuthFromCookies() {
    // When admin logs out from the backend panel, cookies are cleared in a different page.
    // Sync state when the SPA regains focus so the UI updates without manual refresh.
    const token = Cookies.get("token");
    let roles = [];
    try {
      roles = JSON.parse(Cookies.get("roles") || "[]");
    } catch {
      roles = [];
    }
    const isAdmin = Boolean(token) && Array.isArray(roles) && roles.includes("ROLE_ADMIN");
    this.setState({
      isAdminLoggedIn: isAdmin,
      isDefaultLoggedIn: Boolean(token),
    });
  }

  // Function to handle user activity
  handleUserActivity = () => {
    if (this.sessionTimeout) clearTimeout(this.sessionTimeout);
    this.sessionTimeout = setTimeout(this.handleLogout, 18000000);
  };

  // Function to handle admin login
  handleAdminLogin = () => {
    this.setState({ isAdminLoggedIn: true });
    this.handleUserActivity(); // Start session timeout
    window.location.assign("/admin/jwt-login");
  };

  // Function to handle default login
  handleDefaultLogin = () => {
    this.setState({ isDefaultLoggedIn: true });
    this.handleUserActivity(); // Start session timeout
  };

  // Function to handle logout
  handleLogout = () => {
    // Clear session data and redirect to login page
    Cookies.remove("token");
    Cookies.remove("username");
    Cookies.remove("email");
    Cookies.remove("phoneNo");
    Cookies.remove("roles");
    Cookies.remove("accountType");
    this.setState({ isAdminLoggedIn: false, isDefaultLoggedIn: false });
    // Redirect to login page, you may use react-router-dom's history for this
    // Example: this.props.history.push('/login');
  };

  render() {
    const { isAdminLoggedIn, isDefaultLoggedIn } = this.state;

    return (
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}>
      <Router>
        <CompareProvider>
        <ShortlistProvider>
        <NotificationProvider>
        <div className="min-h-screen flex flex-col bg-white">
          <Header
            onAdminLogin={this.handleAdminLogin}
            onDefaultLogin={this.handleDefaultLogin}
            onLogout={this.handleLogout}
          />

          <main className="flex-1">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {(isDefaultLoggedIn || Cookies.get("token")) && getAccountType() === "student" && (
                <ProfileCard />
              )}
            </div>

            <Suspense fallback={<PageLoader />}>
            <PageTransition>
              <Route path="/" element={<Home />} />
              <Route path="/search" element={<SearchResultsPage />} />
              <Route path="/student/dashboard" element={<StudentDashboardPage />} />
              <Route path="/college/dashboard" element={<CollegeDashboardPage />} />
              <Route path="/author/dashboard" element={<AuthorDashboardPage />} />
              <Route
                path="/college/:course/college/location/:menuName/*"
                element={<CollegeByLocationRedirect />}
              />
              <Route path="/college/:course/:menuName" element={<CollegeListRedirect />} />
              <Route
                path="/college/:course/:ranking/:menuName"
                element={<CollegeListPage />}
              />
              <Route path="/college/:collegeUrl" element={<CollegeDetailsPage />} />
              <Route path="/compare" element={<ComparePage />} />
              <Route path="/admission-assistance" element={<AdmissionAssistancePage />} />
              <Route path="/study-abroad" element={<StudyAbroadPage />} />
              <Route path="/cutoffs" element={<CutoffSearchPage />} />
              <Route path="/eligibility-checker" element={<EligibilityCheckerPage />} />
              <Route path="/userprofile/edit" element={<EditStudentProfilePage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/articles" element={<ArticleListPage />} />
              <Route path="/articles/:slug" element={<ArticleDetailPage />} />
              <Route path="/experts/:slug" element={<ExpertProfilePage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="*" element={<NotFoundPage />} />{" "}
              {/* This catches all other routes */}
            </PageTransition>
            </Suspense>

            {this.state.showForm && (
              <EnquiryPopupConditional onClose={() => this.setState({ showForm: false })} />
            )}
          </main>

          <Footer />

          <WhatsAppFloatingWidget />
          <CompareTray />
          <AiChatWidget />
        </div>
        </NotificationProvider>
        </ShortlistProvider>
        </CompareProvider>
      </Router>
      </GoogleOAuthProvider>
    );
  }
}

export default App;
